import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, actionKey } from '../api';
import { useSession, type Me } from '../session';
import { Icon } from '../brand';
import { Loading } from '../ui';
import { BusinessForm, PhoneVerify, type BusinessInput, type Side } from './shared/Onboarding';

const SIDES: { side: Side; icon: 'truck' | 'fuel'; title: string; text: string }[] = [
  { side: 'BUYER', icon: 'truck', title: 'Fleet / transport business', text: 'Approve fuel for your drivers and track what each vehicle uses.' },
  { side: 'SUPPLIER', icon: 'fuel', title: 'Petrol pump', text: 'Give fuel on credit to fleets, record every fill and collect payments.' },
];

/**
 * First stop after Google sign-in for anyone without a business: confirm the
 * mobile (which brings in access a business already gave that number), then
 * register a fleet or pump.
 */
export function Welcome() {
  const { me, refresh, choose } = useSession();
  const nav = useNavigate();
  const [side, setSide] = useState<Side | null>(null);
  if (!me) return <Loading />;

  const afterVerify = async () => {
    await refresh();
    const fresh = await api.get<Me>('/api/me', { orgId: null });
    if (fresh.pendingInvitations.length) nav('/choose');
    else if (fresh.memberships.length) nav('/app');
  };

  const register = async (v: BusinessInput) => {
    const key = actionKey(`register-${side}`);
    const r = await api.post<{ orgId: string }>('/api/register', { side, org: v.org, location: v.location }, { orgId: null, idempotencyKey: key.key });
    key.done();
    await refresh();
    choose(r.orgId);
    nav(side === 'BUYER' ? '/buyer' : '/supplier/setup');
  };

  if (!me.user.phoneVerified) {
    return (
      <div className="card stack" style={{ maxWidth: 560 }}>
        <h1>Welcome, {me.user.name}</h1>
        <p>First, confirm your mobile number. If a fleet or pump has already added you, your access appears straight away.</p>
        <PhoneVerify onVerified={afterVerify} />
      </div>
    );
  }

  if (!side) {
    return (
      <div className="stack" style={{ maxWidth: 560 }}>
        <h1>Register your business</h1>
        <p className="muted">Mobile {me.user.phoneMasked} is confirmed. What do you run?</p>
        {SIDES.map((s) => (
          <button key={s.side} className="card choice-card" onClick={() => setSide(s.side)}>
            <Icon name={s.icon} size={32} />
            <span>
              <strong>{s.title}</strong>
              <small>{s.text}</small>
            </span>
          </button>
        ))}
        <p className="muted">Working for a fleet or pump that already uses SuperIndent? Ask them to add your mobile number. You don't need to register.</p>
      </div>
    );
  }

  return (
    <div className="card stack" style={{ maxWidth: 560 }}>
      <button className="btn ghost small" onClick={() => setSide(null)}><Icon name="back" size={18} /> Back</button>
      <h1>{side === 'BUYER' ? 'Your fleet' : 'Your pump'}</h1>
      <BusinessForm side={side} submitLabel="Create my business" onSubmit={register} />
    </div>
  );
}
